import type {
  AccessApi,
  AccessChange,
  MutationResult,
  OperatorDetail,
} from "./accessApi";
import { operatorName, roleLabel } from "./accessFormat";
import { fill, type AccessTranslate } from "./useAccessT";

export type PendingChange =
  | { kind: "assign"; roleId: string }
  | { kind: "unassign"; roleId: string }
  | { kind: "deactivate" }
  | { kind: "activate" };

export interface ChangeDescription {
  title: string;
  summary: string;
  warning: string;
  run: (change: AccessChange) => Promise<MutationResult>;
}

/** Texts and the API call of one pending change on an operator. */
export function describeChange(
  pending: PendingChange,
  operator: OperatorDetail,
  roleName: (roleId: string) => string,
  api: AccessApi,
  t: AccessTranslate,
): ChangeDescription {
  const operatorLabel = operatorName(operator);
  const id = operator.operator_id;
  switch (pending.kind) {
    case "assign":
      return {
        title: t("admin.access.change.assign.title"),
        summary: fill(t("admin.access.change.assign.summary"), {
          role: roleName(pending.roleId),
          operator: operatorLabel,
        }),
        warning: t("admin.access.change.assign.warning"),
        run: (change) => api.assignRole(id, pending.roleId, change),
      };
    case "unassign":
      return {
        title: t("admin.access.change.unassign.title"),
        summary: fill(t("admin.access.change.unassign.summary"), {
          role: roleName(pending.roleId),
          operator: operatorLabel,
        }),
        warning: t("admin.access.change.unassign.warning"),
        run: (change) => api.unassignRole(id, pending.roleId, change),
      };
    case "deactivate":
      return {
        title: t("admin.access.change.deactivate.title"),
        summary: fill(t("admin.access.change.deactivate.summary"), {
          operator: operatorLabel,
        }),
        warning: t("admin.access.change.deactivate.warning"),
        run: (change) => api.deactivate(id, change),
      };
    case "activate":
      return {
        title: t("admin.access.change.activate.title"),
        summary: fill(t("admin.access.change.activate.summary"), {
          operator: operatorLabel,
        }),
        warning: t("admin.access.change.activate.warning"),
        run: (change) => api.activate(id, change),
      };
  }
}

/** Display name of a role id from the loaded catalog. */
export function roleNameLookup(
  roles: readonly { role_id: string; display_name: string }[],
  t: AccessTranslate,
): (roleId: string) => string {
  return (roleId) =>
    roleLabel(
      t,
      roleId,
      roles.find((role) => role.role_id === roleId)?.display_name ?? roleId,
    );
}
